import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2, TrendingUp, Banknote, Users, AlertCircle,
  Calendar,
} from 'lucide-react';
import { supabase } from '@/services/supabase';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PermissionGate } from '@/components/PermissionGate';

type SubscriptionStatus = 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'CANCELED' | 'EXPIRED';

interface SubscriptionRow {
  id: string;
  account_id: string | null;
  plan_id: string | null;
  status: SubscriptionStatus;
  locked_price: number | null;
  amount: number | null;
  trial_patrol_used: boolean;
  started_at: string | null;
  ends_at: string | null;
  created_at: string;
}

interface PlanRow {
  id: string;
  code: string;
  name: string;
  price_amount: number;
  price_currency: string;
  billing_period: 'trial' | 'monthly' | 'annual';
}

interface InvoiceRow {
  id: string;
  status: string;
  total_amount: number;
  created_at: string;
  paid_at: string | null;
}

const STATUS_COLORS: Record<SubscriptionStatus, string> = {
  TRIAL:    'bg-amber-500/10 text-amber-600',
  ACTIVE:   'bg-emerald-500/10 text-emerald-600',
  PAST_DUE: 'bg-red-500/10 text-red-600',
  CANCELED: 'bg-gray-500/10 text-gray-500',
  EXPIRED:  'bg-gray-500/10 text-gray-500',
};

const STATUS_LABELS: Record<SubscriptionStatus, string> = {
  TRIAL:    'En prueba',
  ACTIVE:   'Activas',
  PAST_DUE: 'Vencidas',
  CANCELED: 'Canceladas',
  EXPIRED:  'Expiradas',
};

function formatQ(n: number) {
  return `Q${n.toFixed(2)}`;
}

export function RevenuePage() {
  const [plans, setPlans] = useState<PlanRow[]>([]);
  const [subs, setSubs]   = useState<SubscriptionRow[]>([]);
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [invoicesAvailable, setInvoicesAvailable] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [pRes, sRes] = await Promise.all([
        supabase.from('subscription_plans').select('id, code, name, price_amount, price_currency, billing_period'),
        supabase.from('subscriptions').select('id, account_id, plan_id, status, locked_price, amount, trial_patrol_used, started_at, ends_at, created_at'),
      ]);
      if (pRes.error) throw pRes.error;
      if (sRes.error) throw sRes.error;
      setPlans((pRes.data ?? []) as PlanRow[]);
      setSubs((sRes.data ?? []) as SubscriptionRow[]);

      // Invoices are optional (table may not exist on all environments yet — F9 introduces it).
      const iRes = await supabase
        .from('invoices')
        .select('id, status, total_amount, created_at, paid_at')
        .order('created_at', { ascending: false })
        .limit(200);
      if (iRes.error) {
        setInvoicesAvailable(false);
        setInvoices([]);
      } else {
        setInvoicesAvailable(true);
        setInvoices((iRes.data ?? []) as InvoiceRow[]);
      }
    } catch (e: unknown) {
      setError((e as { message?: string })?.message ?? 'Error cargando datos de revenue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Realtime: refresh automatically when plans, subscriptions or invoices change.
  useEffect(() => {
    const channel = supabase
      .channel(`revenue-realtime-${Date.now()}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'subscriptions' }, () => { void load(); })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'subscription_plans' }, () => { void load(); })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'invoices' }, () => { void load(); })
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [load]);

  const planById = useMemo(() => {
    const map = new Map<string, PlanRow>();
    for (const p of plans) map.set(p.id, p);
    return map;
  }, [plans]);

  const activeSubs = useMemo(
    () => subs.filter((s) => s.status === 'ACTIVE'),
    [subs],
  );
  const trialSubs = useMemo(
    () => subs.filter((s) => s.status === 'TRIAL'),
    [subs],
  );

  // MRR = sum of locked_price (or plan price) for ACTIVE monthly subs +
  // ACTIVE annual subs / 12.
  const mrr = useMemo(() => {
    let total = 0;
    for (const s of activeSubs) {
      const plan = s.plan_id ? planById.get(s.plan_id) : null;
      const price = Number(s.locked_price ?? s.amount ?? plan?.price_amount ?? 0);
      if (!plan || plan.billing_period === 'monthly') {
        total += price;
      } else if (plan.billing_period === 'annual') {
        total += price / 12;
      }
    }
    return total;
  }, [activeSubs, planById]);

  const arr = mrr * 12;

  // Group active subs by plan to show breakdown.
  const breakdown = useMemo(() => {
    const map = new Map<string, { plan: PlanRow | null; count: number; mrr: number }>();
    for (const s of activeSubs) {
      const plan = s.plan_id ? planById.get(s.plan_id) ?? null : null;
      const key = plan?.id ?? '__unknown__';
      const entry = map.get(key) ?? { plan, count: 0, mrr: 0 };
      entry.count += 1;
      const price = Number(s.locked_price ?? s.amount ?? plan?.price_amount ?? 0);
      if (!plan || plan.billing_period === 'monthly') entry.mrr += price;
      else if (plan.billing_period === 'annual') entry.mrr += price / 12;
      map.set(key, entry);
    }
    return Array.from(map.values()).sort((a, b) => b.mrr - a.mrr);
  }, [activeSubs, planById]);

  // Invoices: paid this month, pending, overdue.
  const invoiceStats = useMemo(() => {
    const now = new Date();
    const startMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    let paidMonth = 0;
    let pending = 0;
    let paidThisMonthAmount = 0;
    let pendingAmount = 0;
    for (const inv of invoices) {
      if (inv.status === 'paid' && inv.paid_at && new Date(inv.paid_at).getTime() >= startMonth) {
        paidMonth += 1;
        paidThisMonthAmount += Number(inv.total_amount);
      }
      if (inv.status === 'issued' || inv.status === 'overdue' || inv.status === 'pending') {
        pending += 1;
        pendingAmount += Number(inv.total_amount);
      }
    }
    return { paidMonth, pending, paidThisMonthAmount, pendingAmount };
  }, [invoices]);

  return (
    <PermissionGate require="revenue.view">
      <div className="container mx-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <TrendingUp className="w-6 h-6" />
              Ingresos
            </h1>
            <p className="text-sm text-muted-foreground">
              Métricas de ingresos recurrentes (MRR / ARR), distribución de suscripciones activas y estado
              de facturación. Se actualiza automáticamente en tiempo real.
            </p>
          </div>
        </div>

        {error && (
          <Card className="border-destructive">
            <CardContent className="pt-6 flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm">{error}</span>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Cargando datos...
          </div>
        ) : (
          <>
            {/* KPI cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
                    <Banknote className="w-4 h-4" /> MRR
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{formatQ(mrr)}</div>
                  <div className="text-xs text-muted-foreground">Ingreso recurrente mensual</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
                    <Calendar className="w-4 h-4" /> ARR
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{formatQ(arr)}</div>
                  <div className="text-xs text-muted-foreground">Proyección anual (MRR × 12)</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
                    <Users className="w-4 h-4" /> Suscripciones activas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{activeSubs.length}</div>
                  <div className="text-xs text-muted-foreground">
                    + {trialSubs.length} en prueba
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-muted-foreground">Estados</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1">
                    {(['TRIAL', 'ACTIVE', 'PAST_DUE', 'CANCELED', 'EXPIRED'] as SubscriptionStatus[]).map((st) => {
                      const count = subs.filter((s) => s.status === st).length;
                      if (count === 0) return null;
                      return (
                        <Badge key={st} className={STATUS_COLORS[st]}>
                          {STATUS_LABELS[st]}: {count}
                        </Badge>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Breakdown by plan */}
            <Card>
              <CardHeader>
                <CardTitle>Distribución por plan (suscripciones activas)</CardTitle>
              </CardHeader>
              <CardContent>
                {breakdown.length === 0 ? (
                  <div className="text-sm text-muted-foreground">Aún no hay suscripciones activas.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left border-b text-xs uppercase text-muted-foreground">
                          <th className="py-2">Plan</th>
                          <th className="py-2 text-right">Activas</th>
                          <th className="py-2 text-right">MRR</th>
                          <th className="py-2 text-right">% del MRR</th>
                        </tr>
                      </thead>
                      <tbody>
                        {breakdown.map((b) => (
                          <tr key={b.plan?.id ?? '__unknown__'} className="border-b last:border-b-0">
                            <td className="py-2">
                              {b.plan ? (
                                <span>
                                  <span className="font-semibold">{b.plan.code}</span>{' '}
                                  <span className="text-muted-foreground">{b.plan.name}</span>
                                </span>
                              ) : (
                                <span className="text-muted-foreground italic">Sin plan asignado</span>
                              )}
                            </td>
                            <td className="py-2 text-right">{b.count}</td>
                            <td className="py-2 text-right font-mono">{formatQ(b.mrr)}</td>
                            <td className="py-2 text-right text-muted-foreground">
                              {mrr > 0 ? `${((b.mrr / mrr) * 100).toFixed(1)}%` : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Invoices */}
            <Card>
              <CardHeader>
                <CardTitle>Facturación</CardTitle>
              </CardHeader>
              <CardContent>
                {!invoicesAvailable ? (
                  <div className="text-sm text-muted-foreground">
                    Aún no hay facturas registradas para este periodo. Cuando se emita la primera factura aparecerán aquí los totales pagados y pendientes.
                  </div>
                ) : invoices.length === 0 ? (
                  <div className="text-sm text-muted-foreground">Sin facturas registradas.</div>
                ) : (
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="border rounded-md p-4">
                      <div className="text-xs uppercase text-muted-foreground">Pagadas este mes</div>
                      <div className="text-2xl font-bold">{invoiceStats.paidMonth}</div>
                      <div className="text-sm text-muted-foreground">
                        {formatQ(invoiceStats.paidThisMonthAmount)}
                      </div>
                    </div>
                    <div className="border rounded-md p-4">
                      <div className="text-xs uppercase text-muted-foreground">Pendientes</div>
                      <div className="text-2xl font-bold">{invoiceStats.pending}</div>
                      <div className="text-sm text-muted-foreground">
                        {formatQ(invoiceStats.pendingAmount)}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </PermissionGate>
  );
}
